import GUI from 'lil-gui';
import type * as THREE from 'three';
import type { ContactShadow } from '../render/ContactShadow';
import {
  applyDefaultPhysics,
  DEFAULT_PHYSICS,
  savePhysicsDefaultsToSource,
  type PhysicsConfig,
} from '../game/Physics';
import {
  applyDefaultCameraTuning,
  cameraNumericKeys,
  clearCachedCameraTuning,
  DEFAULT_CAMERA_TUNING,
  readCameraTuning,
  saveCachedCameraTuning,
  saveCameraDefaultsToSource,
  type CameraNumericKey,
} from '../game/CameraConfig';
import {
  bakeStylePresetAssets,
  isSaveToSourceAvailable,
  saveDefaultsToSource,
  type DefaultsFilePath,
} from '../dev/saveDefaults';
import { motionBlurPrefs } from '../render/MotionBlurPrefs';
import {
  clearPresetOverlay,
  cloneStyleConfig,
  createNewPreset,
  dispatchStyleConfigChange,
  hasPresetOverlay,
  hydrateStyleConfig,
  listMapPresets,
  loadPresetOverlay,
  resetStyleConfig,
  saveStyleConfig,
  savePresetOverlay,
  saveStyleConfigToSource,
  saveStyleToPreset,
  type StyleConfig,
} from '../render/StyleConfig';
import { isValidPresetName, presetForName } from '../render/presets';
import type { FollowCamera } from '../game/Camera';
import type { InputManager } from '../game/Input';
import type { Racer } from '../game/Racer';
import {
  POD_RACER_MODEL_ID,
  saveCachedPodRacerRigTuning,
} from '../game/PodRacerConfig';
import type { PixelationPipeline } from '../render/Pixelation';
import type { ParticlesLayer } from '../scene';
import {
  applyLightingPreset as applyLightingPresetValues,
  type LightingPresetName,
} from './LightingPresets';
import { createIconRail, type IconRail, type IconRailItem } from '../ui/IconRail';
import type { DustTrail, EngineTrail } from '../render/Particles';
import {
  readRendererBackendPreference,
  setRendererBackendPreference,
  type RendererBackendPreference,
  type RendererMode,
} from '../render/Renderer';

export type RenderScaleMode = 'fixed' | 'auto';

export interface RenderScaleTuning {
  mode: RenderScaleMode;
  preset: string;
  scale: number;
  current: number;
  nativeDpr: number;
  minScale: number;
  maxScale: number;
  autoAvailable: boolean;
  autoActive: boolean;
  productionLike: boolean;
  presets: Record<string, number>;
  setMode: (mode: RenderScaleMode) => void;
  setScale: (scale: number) => void;
  setPreset: (preset: string) => void;
  reset: () => void;
}

export interface ShadowMapTuning {
  enabled: boolean;
  frustumSize: number;
  near: number;
  far: number;
  mapSize: number;
  softRadius: number;
  forwardBias: number;
}

export interface CollisionDebugTuning {
  active: boolean;
  source: string;
  impactSpeed: number;
  slideSpeed: number;
  contactSeconds: number;
  normalX: number;
  normalZ: number;
}

export interface TuningTargets {
  physics: PhysicsConfig;
  camera: FollowCamera;
  input: InputManager;
  racer: Racer;
  pixelation: PixelationPipeline;
  style?: StyleConfig;
  onStyleChange?: (style: StyleConfig) => void;
  /** Sun directional light. Provided by main.ts for live shadow tuning. */
  sun?: THREE.DirectionalLight;
  /** Live shadow-map config the panel mutates. */
  shadowConfig?: ShadowMapTuning;
  /** Re-applies the shadow config to the sun (called after size/far/mapSize changes). */
  applyShadowConfig?: () => void;
  /** The racer's contact-shadow decal (additive, sits on top of the cast shadow). */
  contactShadow?: ContactShadow;
  /** Render-scale controls for fixed dev preview and product-style DRS. */
  renderScale?: RenderScaleTuning;
  /** Live wall-contact readout for tuning collision response. */
  collisionDebug?: CollisionDebugTuning;
  /** Live gameplay engine particle trail, for tuning chase-camera exhaust readability. */
  engineTrail?: EngineTrail;
  /** Live gameplay dust trail, for tuning WebGPU dust readability. */
  dustTrail?: DustTrail;
  /** Active renderer mode. WebGPU-only controls are hidden on WebGL. */
  rendererMode?: RendererMode;
  /** Source particles layer that backs the live engine trail. */
  particlesLayer?: {
    file: DefaultsFilePath;
    layer: ParticlesLayer;
  };
  /** Optional toast hook for showing save status. Falls back to console.info. */
  onStatus?: (message: string, kind: 'ok' | 'error') => void;
  /** Map preset binding for the current track. When provided, the panel
   *  shows a preset selector + Save / Save As, and the active preset is
   *  what production will load on next boot. */
  preset?: {
    trackUrl: string;
    /** Empty string when the track has no preset binding (fallback mode). */
    activeName: string;
    isFallback: boolean;
  };
}

export interface TuningPanelOptions {
  /**
   * When false, do not create the visual style folders that are already owned
   * by StylePanel in the unified ScenePanel surface.
   */
  includeVisualSections?: boolean;
}

type TuningSectionKey =
  | 'style'
  | 'save'
  | 'physics'
  | 'collision'
  | 'input'
  | 'camera'
  | 'racer'
  | 'blur'
  | 'asphalt'
  | 'aggregate'
  | 'glint'
  | 'webgpuAsphalt'
  | 'lighting'
  | 'day'
  | 'presets'
  | 'shadow'
  | 'contact'
  | 'renderScale'
  | 'engineTrail'
  | 'pixel';

type TuningNavKey = 'tune' | 'style' | 'physcam' | 'physics' | 'input' | 'camera' | 'racer' | 'save';

interface TuningSection {
  key: TuningSectionKey;
  navKey: TuningNavKey;
  label: string;
  icon: string;
  folder: GUI;
}

const TUNING_NAV_ITEMS: ReadonlyArray<IconRailItem<TuningNavKey>> = [
  { key: 'tune', label: 'Tune', icon: 'Tune', iconPath: '<path d="M4 21v-7"/><path d="M4 10V3"/><path d="M12 21v-9"/><path d="M12 8V3"/><path d="M20 21v-5"/><path d="M20 12V3"/><path d="M2 14h4"/><path d="M10 8h4"/><path d="M18 16h4"/>' },
  { key: 'style', label: 'Style', icon: 'Style', iconPath: '<circle cx="13.5" cy="6.5" r=".5"/><circle cx="17.5" cy="10.5" r=".5"/><circle cx="8.5" cy="7.5" r=".5"/><circle cx="6.5" cy="12.5" r=".5"/><path d="M12 22a10 10 0 1 1 10-10c0 2.2-1.8 4-4 4h-1.5a2.5 2.5 0 0 0 0 5H12z"/>' },
  { key: 'physcam', label: 'PhysCam', icon: 'PhysCam', iconPath: '<path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h9"/><circle cx="12" cy="13" r="3"/><path d="M19 14v6"/><path d="M16 17h6"/>' },
  { key: 'physics', label: 'Physics', icon: 'Physics', iconPath: '<path d="m21 16-9 5-9-5V8l9-5 9 5z"/><path d="m3.3 7.7 8.7 5 8.7-5"/><path d="M12 22V12"/>' },
  { key: 'input', label: 'Input', icon: 'Input', iconPath: '<path d="M6 11h4"/><path d="M8 9v4"/><path d="M15 12h.01"/><path d="M18 10h.01"/><path d="M17.3 5H6.7A4.7 4.7 0 0 0 2 9.7v4.6A4.7 4.7 0 0 0 6.7 19h10.6a4.7 4.7 0 0 0 4.7-4.7V9.7A4.7 4.7 0 0 0 17.3 5z"/>' },
  { key: 'camera', label: 'Camera', icon: 'Camera', iconPath: '<path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3z"/><circle cx="12" cy="13" r="3"/>' },
  { key: 'racer', label: 'Racer', icon: 'Racer', iconPath: '<path d="M12 5v14"/><path d="M8 7c-2 1-3 3-3 5s1 4 3 5"/><path d="M16 7c2 1 3 3 3 5s-1 4-3 5"/><path d="M8 12h8"/>' },
  { key: 'save', label: 'Save', icon: 'Save', iconPath: '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M9 15h6"/>' },
];

export class TuningPanel {
  private readonly gui: GUI;
  readonly root: HTMLElement;
  private readonly targets: TuningTargets;
  private readonly statusElement: HTMLDivElement;
  private activePresetName: string;
  private presetSelectController: ReturnType<GUI['add']> | null = null;
  private presetIndicator: HTMLSpanElement | null = null;
  private readonly sections: TuningSection[] = [];
  private readonly sectionPanels = new Map<TuningNavKey, HTMLElement>();
  private sectionRail: IconRail<TuningNavKey> | null = null;

  constructor(targets: TuningTargets, private readonly panelOpts: TuningPanelOptions = {}) {
    injectTuningPanelStyles();
    this.targets = targets;
    this.activePresetName = targets.preset?.activeName ?? '';
    this.gui = new GUI({ title: 'Tuning', autoPlace: false });
    this.gui.domElement.style.position = 'static';
    this.gui.domElement.style.width = '100%';

    this.root = document.createElement('section');
    this.root.className = 'tuning-panel tuning-panel-with-rail';
    this.root.setAttribute('aria-label', 'Tuning controls');

    this.statusElement = document.createElement('div');
    this.statusElement.className = 'tuning-panel-status';

    this.addActions();
    this.addPhysicsFolder();
    this.addCollisionFolder();
    this.addInputFolder();
    this.addCameraFolder();
    this.addRacerFolder();
    this.addMotionBlurFolder();
    if (this.panelOpts.includeVisualSections !== false) {
      this.addTrackFolders();
      this.addLightingFolders();
      this.addShadowFolder();
      this.addContactShadowFolder();
    }
    this.addRenderScaleFolder();
    this.addEngineTrailFolder();
    this.addPixelationFolder();
    this.installSectionRail();
  }

  destroy(): void {
    this.gui.destroy();
  }

  /** Test-only: exposes the lil-gui root so the slider-sweep harness can
   *  enumerate every control. See `src/debug/SliderInventory.ts`. */
  getGui(): GUI {
    return this.gui;
  }

  private addActions(): void {
    if (this.targets.style && this.targets.preset) {
      this.addStylePresetFolder();
    }

    const folder = this.addSectionFolder('save', 'Physics + Camera', 'SV', true);
    folder.open();
    const actions = {
      saveAllToCode: () => void this.saveAllToSource(),
      resetAll: () => this.resetAll(),
    };
    const saveCtrl = folder.add(actions, 'saveAllToCode').name('Save to code');
    if (!isSaveToSourceAvailable()) saveCtrl.disable();
    folder.add(actions, 'resetAll').name('Reset all ↻');
  }

  private addStylePresetFolder(): void {
    const folder = this.addSectionFolder('style', 'Map Style Preset', 'ST', true);
    folder.open();

    const indicator = document.createElement('div');
    indicator.style.padding = '4px 10px 6px';
    indicator.style.fontSize = '11px';
    indicator.style.fontFamily = 'ui-monospace, SFMono-Regular, Menlo, monospace';
    indicator.style.opacity = '0.85';
    const indicatorText = document.createElement('span');
    indicator.appendChild(indicatorText);
    this.presetIndicator = indicatorText;
    folder.domElement.appendChild(indicator);

    const presets = listMapPresets();
    const options: Record<string, string> = {};
    for (const p of presets) options[p.label] = p.name;
    if (this.targets.preset?.isFallback || this.activePresetName === '') {
      options['(unbound — defaults)'] = '';
    }

    const proxy = { preset: this.activePresetName };
    this.presetSelectController = folder
      .add(proxy, 'preset', options)
      .name('preset')
      .onChange((name: string) => this.switchPreset(name));

    const actions = {
      save: () => void this.savePreset(),
      saveAs: () => void this.savePresetAs(),
      reset: () => this.resetPresetOverlay(),
    };
    const canSave = isSaveToSourceAvailable();
    const saveCtrl = folder.add(actions, 'save').name('Save preset');
    if (!canSave) saveCtrl.disable();
    const saveAsCtrl = folder.add(actions, 'saveAs').name('Save as new preset…');
    if (!canSave) saveAsCtrl.disable();
    folder.add(actions, 'reset').name('Discard unsaved ↻');

    this.refreshPresetIndicator();
  }

  private refreshPresetIndicator(): void {
    if (!this.presetIndicator) return;
    const trackUrl = this.targets.preset?.trackUrl ?? '';
    if (this.activePresetName === '') {
      this.presetIndicator.textContent = `⚠ ${trackUrl} has no preset bound — Save As to create one.`;
      this.presetIndicator.style.color = '#f5c87a';
      return;
    }
    const dirty = hasPresetOverlay(this.activePresetName);
    if (dirty) {
      this.presetIndicator.textContent = `● Unsaved changes — production still ships “${this.activePresetName}”.`;
      this.presetIndicator.style.color = '#f5c87a';
    } else {
      this.presetIndicator.textContent = `→ Production ships “${this.activePresetName}” for ${trackUrl}.`;
      this.presetIndicator.style.color = '#7ad27a';
    }
  }

  private switchPreset(name: string): void {
    const style = this.targets.style;
    if (!style) return;
    if (name === '') {
      this.activePresetName = '';
      this.refreshPresetIndicator();
      return;
    }
    const preset = presetForName(name);
    if (!preset) {
      this.flashStatus(`Unknown preset: ${name}`, false);
      return;
    }
    this.activePresetName = name;
    const overlay = loadPresetOverlay(name);
    const next = overlay ?? hydrateStyleConfig(preset.data);
    Object.assign(style, cloneStyleConfig(next));
    this.gui.controllersRecursive().forEach((c) => c.updateDisplay());
    dispatchStyleConfigChange(style);
    this.targets.onStyleChange?.(style);
    this.refreshPresetIndicator();
    this.flashStatus(`Loaded preset “${preset.label}”${overlay ? ' (with unsaved overlay)' : ''}`, true);
  }

  private async savePreset(): Promise<void> {
    const style = this.targets.style;
    if (!style) return;
    if (this.activePresetName === '') {
      this.flashStatus('No preset selected — use Save As to create one.', false);
      return;
    }
    this.flashStatus(`Writing preset “${this.activePresetName}”…`, true);
    const result = await saveStyleToPreset(this.activePresetName, cloneStyleConfig(style));
    if (result.ok) {
      const bake = this.targets.preset?.trackUrl
        ? await bakeStylePresetAssets(this.activePresetName, this.targets.preset.trackUrl, cloneStyleConfig(style))
        : null;
      const time = new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' });
      const bakeTail = bake && !bake.ok ? `; crack bake failed: ${bake.error ?? 'unknown'}` : '; baked cracks';
      this.flashStatus(`✓ Saved preset “${this.activePresetName}” at ${time}${bakeTail} (reload to verify)`, true);
      this.refreshPresetIndicator();
    } else {
      this.flashStatus(`Save failed — ${result.error ?? 'unknown'}`, false);
    }
  }

  private async savePresetAs(): Promise<void> {
    const style = this.targets.style;
    if (!style) return;
    const trackUrl = this.targets.preset?.trackUrl;
    const raw = window.prompt(
      'New preset name (lowercase letters, digits, dashes; e.g. "hero-night"):',
      this.activePresetName ? `${this.activePresetName}-copy` : 'new-preset',
    );
    if (raw === null) return;
    const name = raw.trim().toLowerCase();
    if (!isValidPresetName(name)) {
      this.flashStatus(`Invalid preset name: "${raw}"`, false);
      return;
    }
    if (presetForName(name)) {
      this.flashStatus(`Preset "${name}" already exists — pick a different name.`, false);
      return;
    }
    const bindToTrack = trackUrl
      ? window.confirm(`Bind "${name}" to ${trackUrl}? Production will load it for this map on next boot.`)
      : false;
    this.flashStatus(`Creating preset “${name}”…`, true);
    const result = await createNewPreset(name, cloneStyleConfig(style), {
      bindToTrack: bindToTrack ? trackUrl : undefined,
    });
    if (!result.ok) {
      this.flashStatus(`Create failed — ${result.error ?? 'unknown'}`, false);
      return;
    }
    const time = new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' });
    const bake = bindToTrack && trackUrl
      ? await bakeStylePresetAssets(name, trackUrl, cloneStyleConfig(style))
      : null;
    const tail = bindToTrack ? ' and bound to current track' : '';
    const bakeTail = bake && !bake.ok ? `; crack bake failed: ${bake.error ?? 'unknown'}` : (bake ? '; baked cracks' : '');
    this.flashStatus(`✓ Created preset “${name}”${tail} at ${time}${bakeTail}. Reload to pick up the new file.`, true);
    // The new preset only shows up in the dropdown after a Vite reload picks
    // up the new JSON file. Tell the user explicitly so they don't think the
    // dropdown is broken.
    this.activePresetName = name;
    if (this.presetSelectController && bindToTrack) {
      // Force the user to reload by hinting in the indicator.
    }
    this.refreshPresetIndicator();
  }

  private resetPresetOverlay(): void {
    if (this.activePresetName === '') return;
    clearPresetOverlay(this.activePresetName);
    const preset = presetForName(this.activePresetName);
    if (preset && this.targets.style) {
      Object.assign(this.targets.style, cloneStyleConfig(preset.data));
      this.gui.controllersRecursive().forEach((c) => c.updateDisplay());
      dispatchStyleConfigChange(this.targets.style);
      this.targets.onStyleChange?.(this.targets.style);
    }
    this.refreshPresetIndicator();
    this.flashStatus(`Discarded overlay — “${this.activePresetName}” reloaded from disk`, true);
  }

  private async saveAllToSource(): Promise<void> {
    // Style flows through the preset system above; this button now only
    // covers physics + camera defaults. If there's no active preset binding
    // for the current map (fallback mode), fall back to writing
    // StyleConfig.defaults.json so legacy tracks aren't left without a save
    // path.
    this.flashStatus('Writing physics + camera defaults…', true);
    const physics = await savePhysicsDefaultsToSource(this.targets.physics);
    const camera = await saveCameraDefaultsToSource(readCameraTuning(this.targets.camera));
    const fallbackStyle = this.targets.style && this.targets.preset?.isFallback
      ? await saveStyleConfigToSource(this.targets.style)
      : { ok: true as const, bytes: 0 };
    if (physics.ok && camera.ok && fallbackStyle.ok) {
      const time = new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' });
      const styleNote = this.targets.preset?.isFallback ? ' + style fallback' : '';
      this.flashStatus(
        `✓ Saved physics + camera${styleNote} at ${time} (${physics.bytes ?? '?'}b + ${camera.bytes ?? '?'}b)`,
        true,
      );
    } else {
      const errs = [
        !physics.ok ? `physics: ${physics.error ?? 'unknown'}` : null,
        !camera.ok ? `camera: ${camera.error ?? 'unknown'}` : null,
        !fallbackStyle.ok ? `style: ${fallbackStyle.error ?? 'unknown'}` : null,
      ].filter(Boolean).join(' • ');
      this.flashStatus(`Save failed — ${errs}`, false);
    }
  }

  private resetAll(): void {
    applyDefaultPhysics(this.targets.physics);
    clearCachedCameraTuning();
    applyDefaultCameraTuning(this.targets.camera);
    if (this.targets.style) {
      if (this.activePresetName) {
        // Preset mode: drop the overlay and reload from the preset's JSON.
        this.resetPresetOverlay();
      } else {
        // Fallback (no preset bound): legacy global reset.
        const nextStyle = resetStyleConfig();
        Object.assign(this.targets.style, cloneStyleConfig(nextStyle));
        this.commitStyle();
      }
    }
    this.gui.controllersRecursive().forEach((c) => c.updateDisplay());
    this.flashStatus('Physics + camera reset to committed defaults', true);
  }

  private flashStatus(message: string, ok: boolean): void {
    this.targets.onStatus?.(message, ok ? 'ok' : 'error');
    this.statusElement.textContent = message;
    this.statusElement.style.color = ok ? '#7ad27a' : '#f57a7a';
    this.statusElement.style.backgroundColor = ok
      ? 'rgba(122, 210, 122, 0.14)'
      : 'rgba(245, 122, 122, 0.16)';
    window.setTimeout(() => {
      if (this.statusElement.textContent === message) {
        this.statusElement.textContent = '';
        this.statusElement.style.backgroundColor = 'transparent';
      }
    }, 4000);
  }

  private addPhysicsFolder(): void {
    const f = this.addSectionFolder('physics', 'Physics', 'PH', true);
    f.add(this.targets.physics, 'maxSpeed', 20, 200, 1);
    f.add(this.targets.physics, 'accel', 5, 120, 1);
    f.add(this.targets.physics, 'brakeStrength', 5, 200, 1);
    f.add(this.targets.physics, 'drag', 0, 4, 0.05);
    f.add(this.targets.physics, 'grip', 0, 30, 0.1);
    f.add(this.targets.physics, 'turnRate', 0.5, 8, 0.05);
    const fs = f.addFolder('Steering feel');
    fs.add(this.targets.physics, 'steerCurve', 1, 3, 0.05).name('steer curve');
    fs.add(this.targets.physics, 'lowSpeedSteerMultiplier', 0, 1, 0.01).name('low-speed mult');
    fs.add(this.targets.physics, 'highSpeedSteerMultiplier', 0.15, 1, 0.01).name('hi-speed mult');
    fs.add(this.targets.physics, 'rotationalDrag', 0, 12, 0.1).name('rot drag');
    fs.add(this.targets.physics, 'weatherVaneStrength', 0, 8, 0.05).name('weather vane');
    fs.add(this.targets.physics, 'turnInGripStrength', 0, 8, 0.05).name('turn-in grip');
    fs.add(this.targets.physics, 'turnInGripYawThreshold', 0.1, 4, 0.05).name('turn-in yaw sat');
    fs.add(this.targets.physics, 'driftGripFactor', 0, 1, 0.01).name('drift grip');
    fs.open();
    const fb = f.addFolder('Drift boost');
    fb.add(this.targets.physics, 'driftChargeRate', 0, 2, 0.01).name('charge rate');
    fb.add(this.targets.physics, 'driftChargeDecayRate', 0, 1, 0.01).name('decay rate');
    fb.add(this.targets.physics, 'driftChargeYawSat', 0.1, 5, 0.05).name('yaw sat');
    fb.add(this.targets.physics, 'driftSlipEnterThreshold', 0, 1, 0.01).name('slip enter');
    fb.add(this.targets.physics, 'driftSlipExitThreshold', 0, 1, 0.01).name('slip exit');
    fb.add(this.targets.physics, 'driftBoostThrust', 0, 120, 1).name('boost thrust');
    fb.add(this.targets.physics, 'driftBoostMaxSpeedMultiplier', 1, 1.5, 0.01).name('boost cap');
    fb.add(this.targets.physics, 'boostConsumeRate', 0, 2, 0.01).name('consume rate');
    f.add(this.targets.physics, 'offTrackDrag', 0, 12, 0.1);
    f.add(this.targets.physics, 'offTrackGrip', 0, 12, 0.1);
    f.add(this.targets.physics, 'reverseSpeed', 0, 40, 1);
    f.add(this.targets.physics, 'brakeGripMultiplier', 0.5, 4, 0.05);
    f.add(this.targets.physics, 'brakeTurnMultiplier', 0.5, 3, 0.05);
    f.open();
  }

  private addCollisionFolder(): void {
    const f = this.addSectionFolder('collision', 'Collision', 'CL', true);
    const physics = this.targets.physics;
    f.add(physics, 'collisionRestitution', 0, 0.5, 0.01).name('wall bounce');
    f.add(physics, 'collisionSlideFriction', 0, 0.6, 0.01).name('impact slide drag');
    f.add(physics, 'collisionContactFriction', 0, 4, 0.05).name('grind friction');
    f.add(physics, 'collisionPersistentSeconds', 0, 0.2, 0.005).name('grind after');
    f.add(physics, 'collisionMinBounceSpeed', 0, 30, 0.5).name('min bounce speed');
    f.add(physics, 'collisionSeparationSpeed', 0, 2, 0.05).name('separation cap');
    f.add(physics, 'collisionSameNormalDot', 0.5, 0.99, 0.01).name('same wall dot');

    const visual = f.addFolder('Pod reaction');
    visual.add(physics, 'collisionEngineKick', 0, 8, 0.1).name('engine kick');
    visual.add(physics, 'collisionEngineSqueeze', 0, 6, 0.1).name('engine squeeze');
    visual.add(physics, 'collisionCockpitPunch', 0, 0.6, 0.01).name('cockpit punch');
    visual.open();

    const debug = this.targets.collisionDebug;
    if (debug) {
      const live = f.addFolder('Live contact');
      live.add(debug, 'active').name('active').listen().disable();
      live.add(debug, 'source').name('source').listen().disable();
      live.add(debug, 'impactSpeed').name('impact m/s').listen().disable();
      live.add(debug, 'slideSpeed').name('slide m/s').listen().disable();
      live.add(debug, 'contactSeconds').name('same wall s').listen().disable();
      live.add(debug, 'normalX').name('normal x').listen().disable();
      live.add(debug, 'normalZ').name('normal z').listen().disable();
      live.open();
    }
    f.open();
  }

  private addInputFolder(): void {
    const fi = this.addSectionFolder('input', 'Input', 'IN');
    fi.add(this.targets.input, 'steerRiseRate', 0.5, 8, 0.1).name('steer ramp');
    fi.add(this.targets.input, 'steerReturnRate', 1, 20, 0.1).name('steer return');
    fi.add(this.targets.input, 'pedalRiseRate', 2, 20, 0.5).name('pedal rise');
    fi.add(this.targets.input, 'pedalReturnRate', 2, 24, 0.5).name('pedal return');
  }

  private addCameraFolder(): void {
    const f2 = this.addSectionFolder('camera', 'Camera', 'CA');
    f2.add(this.targets.camera, 'mode', ['topDown', 'chase']).name('mode');
    f2.add(this.targets.camera, 'height', 3, 100, 0.5).name('top height');
    f2.add(this.targets.camera, 'tiltDeg', 25, 90, 1).name('top tilt (deg)');
    f2.add(this.targets.camera, 'topLookAhead', 0, 80, 0.5).name('top lookahead');
    f2.add(this.targets.camera, 'topLeadResponsiveness', 1, 20, 0.5).name('top lead response');
    f2.add(this.targets.camera, 'topLeadFullSpeed', 10, 120, 1).name('lead full speed');
    f2.add(this.targets.camera, 'topFollowResponsiveness', 0, 30, 0.5).name('top follow lag');
    f2.add(this.targets.camera, 'chaseDistance', 4, 45, 0.5).name('chase distance');
    f2.add(this.targets.camera, 'chaseHeight', 1.5, 18, 0.25).name('chase height');
    f2.add(this.targets.camera, 'chaseLookAhead', 2, 60, 0.5).name('chase lookahead');
    f2.add(this.targets.camera, 'chaseTargetHeight', 0, 5, 0.1).name('chase target h');
    f2.add(this.targets.camera, 'chaseFovDeg', 35, 90, 1).name('chase fov (idle)');
    f2.add(this.targets.camera, 'chaseFovBoostDeg', 50, 110, 1).name('chase fov (boost)');
    f2.add(this.targets.camera, 'chaseFovFullSpeed', 20, 120, 1).name('fov full speed');
    f2.add(this.targets.camera, 'chaseFovResponsiveness', 0.05, 1.50, 0.05).name('fov halflife');
    f2.add(this.targets.camera, 'chaseHeadingVelocityBlend', 0, 1, 0.05).name('velocity yaw blend');
    f2.add(this.targets.camera, 'chaseHeadingDeadzoneYawRate', 0, 0.5, 0.01).name('yaw deadzone');
    f2.add(this.targets.camera, 'chaseHeadingDeadzoneLateralSpeed', 0, 6, 0.1).name('lateral deadzone');
    f2.add(this.targets.camera, 'chaseMaxYawLagDeg', 0, 90, 1).name('max yaw lag');
    f2.add(this.targets.camera, 'chaseTurnHeightBoost', 0, 8, 0.25).name('turn height boost');
    f2.add(this.targets.camera, 'chaseTurnHeightFullLateral', 4, 40, 1).name('turn height full');
    f2.add(this.targets.camera, 'highSpeedShakeAmp', 0, 0.6, 0.01).name('hi-speed wobble');
    f2.add(this.targets.camera, 'chaseVelocityCompensation', 0, 0.25, 0.01).name('chase speed comp');
    f2.add(this.targets.camera, 'accelLagMaxDistance', 0, 8, 0.1).name('accel pullback');
    f2.add(this.targets.camera, 'accelLagResponsiveness', 1, 20, 0.5).name('accel pull in');
    f2.add(this.targets.camera, 'accelLagReturnResponsiveness', 1, 12, 0.25).name('accel return');
    f2.add(this.targets.camera, 'chasePositionResponsiveness', 0.02, 0.50, 0.01).name('chase pos halflife');
    f2.add(this.targets.camera, 'chaseLookResponsiveness', 0.02, 0.80, 0.01).name('chase yaw halflife');
    const fl = f2.addFolder('Look into turn');
    fl.add(this.targets.camera, 'apexAimEnabled').name('apex aim on');
    fl.add(this.targets.camera, 'apexLookAhead', 5, 80, 1).name('apex base (m)');
    fl.add(this.targets.camera, 'apexLookAheadSpeedGain', 0, 60, 1).name('apex speed gain');
    fl.add(this.targets.camera, 'apexMaxLateralOffset', 0, 24, 0.5).name('apex side cap (m)');
    fl.add(this.targets.camera, 'apexOffTrackFadeStart', 0, 2, 0.05).name('fade start widths');
    fl.add(this.targets.camera, 'apexOffTrackFadeEnd', 0, 3, 0.05).name('fade end widths');
    fl.add(this.targets.camera, 'lookSteerBias', 0, 20, 0.25).name('extra bias (m)');
    fl.add(this.targets.camera, 'cameraLateralLean', 0, 2, 0.05).name('cam lean (m)');
    fl.add(this.targets.camera, 'bankMaxRollDeg', 0, 25, 0.5).name('bank max (deg)');
    fl.add(this.targets.camera, 'bankHalflife', 0.05, 1.00, 0.01).name('bank halflife');
    fl.open();
    // Persist any panel edit to localStorage too, so reload preserves until "Save to code" lands it.
    f2.onChange(() => saveCachedCameraTuning(readCameraTuning(this.targets.camera)));
  }

  private addRacerFolder(): void {
    const fr = this.addSectionFolder('racer', 'Racer Model', 'RC', true);
    fr.add(this.targets.racer, 'modelYawDeg', -180, 180, 1).name('yaw (°)');
    fr.add(this.targets.racer, 'modelScale', 0.2, 4, 0.05).name('scale');
    fr.add(this.targets.racer, 'maxBankDeg', 0, 90, 1).name('bank max (deg)');
    fr.add(this.targets.racer, 'bankResponsiveness', 1, 20, 0.5).name('bank response');
    fr.add(this.targets.racer, 'trackSurfaceRoughness', 0, 1, 0.01).name('track rough');
    fr.add(this.targets.racer, 'offTrackSurfaceRoughness', 0, 1, 0.01).name('dirt rough');
    fr.add(this.targets.racer, 'hoverIdleAmplitude', 0, 0.12, 0.005).name('hover idle');
    fr.add(this.targets.racer, 'hoverBounceAmplitude', 0, 0.2, 0.005).name('hover bounce');
    fr.add(this.targets.racer, 'hoverVibrationAmplitude', 0, 0.06, 0.002).name('hover vibe');
    fr.add(this.targets.racer, 'hoverPitchDeg', 0, 6, 0.1).name('hover pitch');
    fr.add(this.targets.racer, 'hoverHighSpeedDamping', 0, 1, 0.01).name('hi-speed dampen');
    fr.add(this.targets.racer, 'trackSuspensionTravel', 0, 0.6, 0.01).name('susp travel');
    fr.add(this.targets.racer, 'trackSuspensionResponse', 1, 20, 0.5).name('susp response');
    fr.add(this.targets.racer, 'trackSuspensionMaxOffset', 0, 0.5, 0.01).name('susp max');
    fr.add(this.targets.racer, 'hoverBuoyancyAmplitude', 0, 0.12, 0.002).name('buoyancy amp');
    fr.add(this.targets.racer, 'hoverBuoyancyHz', 0.05, 2, 0.01).name('buoyancy hz');
    fr.add(this.targets.racer, 'hoverSpeedLift', 0, 0.3, 0.005).name('speed lift');
    fr.add(this.targets.racer, 'hoverSpeedLiftResponse', 0.05, 3, 0.05).name('lift response');
    fr.add(this.targets.racer, 'cockpitWhipPhysicsGain', 0, 30, 0.25).name('whip → physics');
    fr.add(this.targets.racer, 'gravityY', 0, 50, 0.5).name('gravity (m/s²)');
    fr.add(this.targets.racer, 'landingCompressionGain', 0, 1.5, 0.01).name('land compress');
    fr.add(this.targets.racer, 'landingImpactThreshold', 0, 8, 0.1).name('land threshold');
    fr.add(this.targets.racer, 'enginePulseScale', 0, 0.03, 0.001).name('engine pulse');
    fr.add(this.targets.racer, 'scrapeBankThresholdDeg', 12, 70, 1).name('scrape angle');
    fr.add(this.targets.racer, 'showForwardArrow').name('forward arrow');
    this.addChassisReactionControls(fr);
    this.addEngineCouplingControls(fr);
    fr.open();
  }

  /**
   * Sliders for tuning fields that live on `podRigTuning` (PodRacerRigTuning)
   * rather than directly on Racer. The bridge is a proxy → setPodRacerRigTuning
   * → localStorage cache, matching the persistence pattern used by RigReview.
   */
  private addPodTuningSubFolder(
    parent: GUI,
    label: string,
    keys: ReadonlyArray<{ key: keyof ReturnType<Racer['getPodRacerRigTuning']>; name: string; min: number; max: number; step: number }>,
  ): void {
    const racer = this.targets.racer;
    const folder = parent.addFolder(label);
    const proxy: Record<string, number> = {};
    const syncFromRacer = (): void => {
      const tuning = racer.getPodRacerRigTuning() as unknown as Record<string, number>;
      for (const { key } of keys) proxy[key as string] = tuning[key as string];
    };
    syncFromRacer();
    for (const { key, name, min, max, step } of keys) {
      folder.add(proxy, key as string, min, max, step).name(name);
    }
    folder.onChange(() => {
      const partial: Record<string, number> = {};
      for (const { key } of keys) partial[key as string] = proxy[key as string];
      racer.setPodRacerRigTuning(partial as Partial<ReturnType<Racer['getPodRacerRigTuning']>>);
      saveCachedPodRacerRigTuning(POD_RACER_MODEL_ID, racer.getPodRacerRigTuning());
    });
    folder.open();
  }

  private addChassisReactionControls(parent: GUI): void {
    this.addPodTuningSubFolder(parent, 'Chassis reaction', [
      { key: 'cockpitReactionRoll', name: 'reaction roll', min: 0, max: 0.025, step: 0.0005 },
      { key: 'cockpitReactionPitch', name: 'reaction pitch', min: 0, max: 0.025, step: 0.0005 },
      { key: 'cockpitWhipStrength', name: 'whip strength', min: 0, max: 0.03, step: 0.0005 },
      { key: 'cockpitWhipStiffness', name: 'whip stiffness', min: 0, max: 80, step: 1 },
      { key: 'cockpitWhipDamping', name: 'whip damping', min: 0, max: 16, step: 0.1 },
      { key: 'cockpitWhipMaxOffset', name: 'whip max offset', min: 0, max: 1.2, step: 0.01 },
    ]);
  }

  /**
   * Engine-to-engine coupling and impact response. Strut* controls how
   * tightly the two engines hold their spread; inertiaMass controls how
   * much sudden chassis acceleration (turn G-forces, wall impacts) gets
   * transferred into the engines as a deflection/compression. Higher
   * inertiaMass = engines fly further on a slam.
   */
  private addEngineCouplingControls(parent: GUI): void {
    this.addPodTuningSubFolder(parent, 'Engine coupling', [
      { key: 'strutStiffness', name: 'strut stiffness', min: 0, max: 80, step: 1 },
      { key: 'strutDamping', name: 'strut damping', min: 0, max: 10, step: 0.1 },
      { key: 'cableStiffness', name: 'cable stiffness', min: 0, max: 300, step: 1 },
      { key: 'cableDamping', name: 'cable damping', min: 0, max: 20, step: 0.1 },
      { key: 'inertiaMass', name: 'inertia mass', min: 0, max: 0.4, step: 0.005 },
      { key: 'restStiffness', name: 'rest stiffness', min: 0, max: 40, step: 0.5 },
      { key: 'engineDamping', name: 'engine damping', min: 0, max: 4, step: 0.05 },
    ]);
  }

  private addMotionBlurFolder(): void {
    // Mirrors the player-facing Settings overlay so the dev workflow can
    // toggle motion blur in the live game scene without leaving the dock.
    // Bound directly to the motionBlurPrefs singleton — main.ts reads the
    // same source per-frame, so the change is immediate. The "live strength"
    // readout updates each frame and confirms the speed-band drive is firing.
    const folder = this.addSectionFolder('blur', 'Motion Blur', 'BL', true);
    folder.open();
    const proxy = {
      enabled: motionBlurPrefs.isEnabled(),
      intensity: motionBlurPrefs.getIntensity(),
    };
    folder.add(proxy, 'enabled').name('enabled').onChange((v: boolean) => {
      motionBlurPrefs.setEnabled(v);
    });
    folder.add(proxy, 'intensity', 0, 1, 0.01).name('intensity').onChange((v: number) => {
      motionBlurPrefs.setIntensity(v);
    });
    folder.add(motionBlurPrefs, 'liveStrength').name('live strength').listen().disable();
    // External edits (e.g. from Settings overlay) sync back into the panel.
    motionBlurPrefs.subscribe((state) => {
      proxy.enabled = state.enabled;
      proxy.intensity = state.intensity;
      folder.controllersRecursive().forEach((c) => c.updateDisplay());
    });
  }

  private addTrackFolders(): void {
    const style = this.targets.style;
    if (!style) return;

    // Asphalt base + aggregate + glint sliders. These used to live only in
    // the lil-gui StylePanel (mounted only in MAP/RIG review surfaces); the
    // GAME-mode TuningPanel didn't expose them, which is why the user said
    // "we used to have the track stuff in here." Mirrored here so dialing
    // in the look during a chase run is possible without leaving GAME.
    const asphalt = this.addSectionFolder('asphalt', 'Asphalt — Base', 'AS');
    asphalt.addColor(style.asphalt, 'baseHex').name('base color').onChange(() => this.commitStyle());
    asphalt.add(style.asphalt, 'baseRoughness', 0.3, 1.0, 0.01).name('base roughness').onChange(() => this.commitStyle());
    asphalt.add(style.asphalt, 'baseEnvIntensity', 0, 2, 0.01).name('base env').onChange(() => this.commitStyle());

    const aggregate = this.addSectionFolder('aggregate', 'Asphalt — Aggregate', 'AG');
    aggregate.addColor(style.asphalt, 'aggregateColorHex').name('aggregate color').onChange(() => this.commitStyle());
    aggregate.add(style.asphalt, 'aggregateIntensity', 0, 1, 0.01).name('aggregate intensity').onChange(() => this.commitStyle());
    aggregate.add(style.asphalt, 'aggregateScale', 0.2, 4, 0.05).name('scale').onFinishChange(() => this.commitStyle());
    aggregate.add(style.asphalt, 'grainTileMeters', 2, 32, 0.5).name('grain tile m').onChange(() => this.commitStyle());
    aggregate.add(style.asphalt, 'grainDetileMix', 0, 1, 0.01).name('detile mix').onChange(() => this.commitStyle());
    aggregate.add(style.asphalt, 'grainElongation', 0.4, 3, 0.05).name('fiber elongation').onFinishChange(() => this.commitStyle());

    const glint = this.addSectionFolder('glint', 'Asphalt — Sun Glint', 'SG');
    glint.addColor(style.trackGlint, 'colorHex').name('glint color').onChange(() => this.commitStyle());
    glint.add(style.trackGlint, 'intensity', 0, 12, 0.01).name('glint intensity').onChange(() => this.commitStyle());
    glint.add(style.trackGlint, 'exponent', 1, 320, 1).name('glint exponent').onChange(() => this.commitStyle());
    glint.add(style.trackGlint, 'broadness', 0, 3, 0.01).name('glint broadness').onChange(() => this.commitStyle());
    glint.add(style.trackGlint, 'normalStrength', 0, 8, 0.01).name('bump strength').onChange(() => this.commitStyle());
    glint.add(style.trackGlint, 'roughnessContrast', 0, 2, 0.01).name('roughness contrast').onChange(() => this.commitStyle());

    // WebGPU-only tuning. WebGL never reads these multipliers, so dialing
    // here cannot regress the WebGL appearance. Useful because the TSL
    // central-difference bump and the WebGL onBeforeCompile bump read the
    // same scalar inputs differently (different aniso path, different
    // tonemap interaction) and the same value produces subtly different
    // output between backends. The renderer selector at the bottom of this
    // folder lets you swap backends to compare the two side by side.
    const webgpu = this.addSectionFolder('webgpuAsphalt', 'WebGPU tuning (WebGL unaffected)', 'WG');
    webgpu.add(style.trackGlint, 'webgpuBumpMultiplier', 0, 4, 0.01)
      .name('webgpu bump ×').onChange(() => this.commitStyle());
    webgpu.add(style.asphalt, 'webgpuAggregateMultiplier', 0, 4, 0.01)
      .name('webgpu aggregate ×').onChange(() => this.commitStyle());
    this.addTaauTuningControls(webgpu);
    this.addRendererBackendSelector(webgpu);
  }

  private addTaauTuningControls(folder: GUI): void {
    // Surfaces SfxWebGPU.setTaauTuning live. Only useful with ?upscale=taau;
    // when TAAU is off, getTaauTuning returns null and we skip the sliders so
    // they can't drive a no-op rebuild. Each change recompiles the resolve
    // shader (Three bakes the constants as TSL float literals), so use
    // onFinishChange to avoid rebuilding on every drag tick.
    const sfx = (window as unknown as {
      __twin?: { bloom?: {
        getTaauTuning?: () => { currentFrameWeight: number; maxVelocityLength: number; rcasSharpness: number } | null;
        setTaauTuning?: (partial: { currentFrameWeight?: number; maxVelocityLength?: number; rcasSharpness?: number }) => void;
      } };
    }).__twin?.bloom;
    const initial = sfx?.getTaauTuning?.();
    if (!sfx?.setTaauTuning || !initial) return;
    const taau = folder.addFolder('TAAU resolve (?upscale=taau)');
    const proxy = { ...initial };
    taau.add(proxy, 'currentFrameWeight', 0, 1, 0.01)
      .name('current-frame weight')
      .onFinishChange((v: number) => sfx.setTaauTuning?.({ currentFrameWeight: v }));
    taau.add(proxy, 'maxVelocityLength', 1, 1024, 1)
      .name('max velocity (input px)')
      .onFinishChange((v: number) => sfx.setTaauTuning?.({ maxVelocityLength: v }));
    taau.add(proxy, 'rcasSharpness', 0, 1, 0.01)
      .name('RCAS sharpness')
      .onFinishChange((v: number) => sfx.setTaauTuning?.({ rcasSharpness: v }));
  }

  private addRendererBackendSelector(folder: GUI): void {
    // Backends are built once at boot, so changing the renderer can't be
    // live — writing setRendererBackendPreference + reloading the page is
    // the honest path. The current diagnostics readout is shown next to
    // the picker so it's obvious what's actually rendering versus what's
    // been requested.
    const readActive = (): string => {
      const twin = (window as unknown as {
        __twin?: { diagnostics?: () => { renderer?: { mode?: string; fallbackReason?: string | null } } };
      }).__twin;
      const info = twin?.diagnostics?.().renderer;
      if (!info?.mode) return 'unknown';
      return info.fallbackReason ? `${info.mode} (fallback: ${info.fallbackReason})` : info.mode;
    };
    const proxy: { backend: RendererBackendPreference; active: string } = {
      backend: readRendererBackendPreference(),
      active: readActive(),
    };
    folder.add(proxy, 'active').name('active renderer').disable();
    folder
      .add(proxy, 'backend', ['auto', 'webgl2', 'webgpu'])
      .name('renderer (reload)')
      .onChange((value: RendererBackendPreference) => {
        setRendererBackendPreference(value);
        const url = new URL(window.location.href);
        // Drop any ?webgl / ?webgpu URL flag so the stored preference wins
        // on reload; otherwise the URL flag would silently override the
        // picker and the next reload would look like the picker is broken.
        url.searchParams.delete('webgl');
        url.searchParams.delete('webgpu');
        window.location.replace(url.toString());
      });
  }

  private addLightingFolders(): void {
    const style = this.targets.style;
    if (!style) return;

    const master = this.addSectionFolder('lighting', 'Master Time of Day', 'LI', true);
    master.open();
    master.add(style.master, 'sunElevationDeg', -20, 90, 0.1).name('sun elev').onChange(() => this.commitStyle());
    master.add(style.master, 'sunAzimuthDeg', -180, 180, 0.1).name('sun azim').onChange(() => this.commitStyle());
    master.add(style.master.atmosphere, 'turbidity', 1, 10, 0.1).onChange(() => this.commitStyle());
    master.add(style.master.atmosphere, 'hazeBoost', 0, 2, 0.01).name('haze').onChange(() => this.commitStyle());
    master.add(style.master.atmosphere, 'warmthBias', -1, 1, 0.01).name('warmth').onChange(() => this.commitStyle());
    master.add(style.master.secondarySun, 'enabled').name('sun B').onChange(() => this.commitStyle());
    master.add(style.master.secondarySun, 'relativeElevationDeg', -60, 60, 0.1).name('sun B rel elev').onChange(() => this.commitStyle());
    master.add(style.master.secondarySun, 'relativeAzimuthDeg', -180, 180, 0.1).name('sun B rel azim').onChange(() => this.commitStyle());
    master.add(style.master.secondarySun, 'intensity', 0, 2, 0.01).name('sun B scale').onChange(() => this.commitStyle());

    const dayCycle = this.addSectionFolder('day', 'Day Cycle', 'DY');
    dayCycle.add(style.dayCycle, 'enabled').onChange(() => this.commitStyle());
    dayCycle.add(style.dayCycle, 'playing').name('play').onChange(() => this.commitStyle());
    dayCycle.add(style.dayCycle, 'scrub', 0, 1, 0.001).onChange(() => this.commitStyle());
    dayCycle.add(style.dayCycle, 'durationSec', 5, 240, 1).name('duration').onChange(() => this.commitStyle());

    const presets = this.addSectionFolder('presets', 'Presets', 'PR');
    const actions = {
      desertHero: () => this.applyLightingPreset('desertHero'),
      hotDesertNoon: () => this.applyLightingPreset('hotDesertNoon'),
      electricSunset: () => this.applyLightingPreset('electricSunset'),
      purpleDusk: () => this.applyLightingPreset('purpleDusk'),
      blueMoonNight: () => this.applyLightingPreset('blueMoonNight'),
      neonStorm: () => this.applyLightingPreset('neonStorm'),
    };
    presets.add(actions, 'desertHero').name('Desert Hero');
    presets.add(actions, 'hotDesertNoon').name('Hot Desert Noon');
    presets.add(actions, 'electricSunset').name('Electric Sunset');
    presets.add(actions, 'purpleDusk').name('Purple Dusk');
    presets.add(actions, 'blueMoonNight').name('Blue Moon Night');
    presets.add(actions, 'neonStorm').name('Neon Storm');
  }

  private commitStyle(): void {
    const style = this.targets.style;
    if (!style) return;
    if (this.activePresetName) {
      savePresetOverlay(this.activePresetName, style);
    } else {
      // No preset bound — keep the legacy global cache so fallback tracks
      // still get session persistence.
      saveStyleConfig(style);
    }
    dispatchStyleConfigChange(style);
    this.targets.onStyleChange?.(style);
    this.refreshPresetIndicator();
  }

  private applyLightingPreset(name: LightingPresetName): void {
    const style = this.targets.style;
    if (!style) return;
    const preset = applyLightingPresetValues(style, name);
    this.gui.controllersRecursive().forEach((c) => c.updateDisplay());
    this.commitStyle();
    this.flashStatus(`Loaded ${preset.label}`, true);
  }

  private addShadowFolder(): void {
    const cfg = this.targets.shadowConfig;
    const sun = this.targets.sun;
    const apply = this.targets.applyShadowConfig;
    if (!cfg || !sun || !apply) return;

    const folder = this.addSectionFolder('shadow', 'Shadow Map', 'SH');
    const density = { metersPerTexel: this.computeShadowTexelDensity(cfg) };
    const refreshDensity = (): void => {
      density.metersPerTexel = this.computeShadowTexelDensity(cfg);
      folder.controllersRecursive().forEach((c) => c.updateDisplay());
    };

    folder.add(cfg, 'enabled').name('shadows on').onChange(() => {
      sun.castShadow = cfg.enabled;
    });
    folder
      .add(cfg, 'frustumSize', 30, 300, 5)
      .name('frustum (±m)')
      .onChange(() => {
        sun.shadow.camera.left = -cfg.frustumSize;
        sun.shadow.camera.right = cfg.frustumSize;
        sun.shadow.camera.top = cfg.frustumSize;
        sun.shadow.camera.bottom = -cfg.frustumSize;
        sun.shadow.camera.updateProjectionMatrix();
        refreshDensity();
      });
    folder
      .add(cfg, 'far', 80, 600, 5)
      .name('far plane (m)')
      .onChange(() => {
        sun.shadow.camera.far = cfg.far;
        sun.shadow.camera.updateProjectionMatrix();
      });
    folder
      .add(cfg, 'mapSize', { '512': 512, '1024': 1024, '2048': 2048, '4096': 4096 })
      .name('map size')
      .onChange(() => {
        apply();
        refreshDensity();
      });
    folder
      .add(cfg, 'softRadius', 0, 4, 0.1)
      .name('PCF radius')
      .onChange(() => {
        sun.shadow.radius = cfg.softRadius;
      });
    folder
      .add(cfg, 'forwardBias', 0, 200, 1)
      .name('forward bias (m)');
    folder.add(density, 'metersPerTexel').name('m / texel').listen().disable();
  }

  private computeShadowTexelDensity(cfg: ShadowMapTuning): number {
    const mapSize = cfg.mapSize > 0 ? cfg.mapSize : 1;
    return Number(((cfg.frustumSize * 2) / mapSize).toFixed(3));
  }

  private addContactShadowFolder(): void {
    const cs = this.targets.contactShadow;
    if (!cs) return;
    const folder = this.addSectionFolder('contact', 'Contact Shadow', 'CS');
    folder.add(cs.config, 'enabled').name('enabled');
    folder.add(cs.config, 'radius', 0.5, 6, 0.05).name('radius (m)');
    folder.add(cs.config, 'opacity', 0, 1, 0.01).name('opacity');
    folder.add(cs.config, 'fadeHeight', 0.2, 8, 0.1).name('fade height (m)');
    folder.add(cs.config, 'falloffPower', 0.5, 6, 0.1).name('falloff');
    folder.add(cs.config, 'groundOffset', 0, 0.2, 0.005).name('ground offset');
  }

  private addPixelationFolder(): void {
    const f3 = this.addSectionFolder('pixel', 'Pixelation', 'PX');
    const pixCfg = this.targets.pixelation.config;
    f3.add(pixCfg, 'enabled');
    const pixRes = { width: pixCfg.width, height: pixCfg.height };
    f3.add(pixRes, 'width', 120, 1920, 10).onChange((v: number) => {
      this.targets.pixelation.setResolution(v, pixRes.height);
    });
    f3.add(pixRes, 'height', 68, 1080, 10).onChange((v: number) => {
      this.targets.pixelation.setResolution(pixRes.width, v);
    });
  }

  private addRenderScaleFolder(): void {
    const renderScale = this.targets.renderScale;
    if (!renderScale) return;

    const folder = this.addSectionFolder('renderScale', 'Render Scale', 'RS');
    const modeOptions: Record<string, RenderScaleMode> = renderScale.autoAvailable
      ? { Fixed: 'fixed', 'Auto DRS': 'auto' }
      : { Fixed: 'fixed' };
    const presetOptions: Record<string, string> = {};
    for (const preset of Object.keys(renderScale.presets)) {
      presetOptions[preset] = preset;
    }
    const refresh = (): void => {
      folder.controllersRecursive().forEach((controller) => controller.updateDisplay());
    };

    folder.add(renderScale, 'mode', modeOptions).name('mode').onChange((mode: RenderScaleMode) => {
      renderScale.setMode(mode);
      refresh();
    });
    folder.add(renderScale, 'preset', presetOptions).name('preview').onChange((preset: string) => {
      renderScale.setPreset(preset);
      refresh();
    });
    folder
      .add(renderScale, 'scale', renderScale.minScale, renderScale.maxScale, 0.01)
      .name('fixed scale')
      .onChange((scale: number) => {
        renderScale.setScale(scale);
        refresh();
      });
    folder.add(renderScale, 'current').name('current').listen().disable();
    folder.add(renderScale, 'nativeDpr').name('native DPR').listen().disable();
    folder.add(renderScale, 'autoActive').name('auto active').listen().disable();
    folder.add(renderScale, 'productionLike').name('prod-like').listen().disable();
    folder.add(renderScale, 'reset').name('Reset 1.00');
  }

  private addEngineTrailFolder(): void {
    const engineTrail = this.targets.engineTrail;
    if (!engineTrail) return;

    const folder = this.addSectionFolder('engineTrail', 'Engine Trail', 'ET');
    const cfg = engineTrail.config;
    const syncLayer = (): void => {
      if (this.targets.particlesLayer) {
        Object.assign(this.targets.particlesLayer.layer.engineTrail, cfg);
        if (this.targets.dustTrail) {
          Object.assign(this.targets.particlesLayer.layer.dustTrail, this.targets.dustTrail.config);
        }
      }
    };
    folder.add(cfg, 'nozzleGlowEnabled').name('nozzle glow');
    folder.add(cfg, 'nozzleGlowSizeBase', 0, 6, 0.05).name('nozzle size');
    folder.add(cfg, 'nozzleGlowSizeIntensity', 0, 8, 0.05).name('nozzle size +thr');
    folder.add(cfg, 'nozzleGlowOpacityBase', 0, 1, 0.01).name('nozzle alpha');
    folder.add(cfg, 'nozzleGlowOpacityIntensity', 0, 1, 0.01).name('nozzle alpha +thr');
    folder.add(cfg, 'sizeBase', 0, 6, 0.05).name('particle size');
    folder.add(cfg, 'sizeIntensity', 0, 8, 0.05).name('size +thr');
    folder.add(cfg, 'sizeJitter', 0, 3, 0.05).name('size jitter');
    folder.add(cfg, 'sizeGrowth', 0, 2, 0.05).name('size growth');
    folder.add(cfg, 'opacityBase', 0, 1, 0.01).name('particle alpha');
    folder.add(cfg, 'opacityIntensity', 0, 1, 0.01).name('alpha +thr');
    folder.add(cfg, 'spawnRateBase', 0, 80, 1).name('spawn base');
    folder.add(cfg, 'spawnRateIntensity', 0, 160, 1).name('spawn +thr');
    folder.add(cfg, 'lifetimeSec', 0.02, 0.8, 0.01).name('life');
    folder.add(cfg, 'lifetimeJitterSec', 0, 0.8, 0.01).name('life jitter');

    if (this.targets.rendererMode === 'webgpu') {
      const dustTrail = this.targets.dustTrail;
      if (dustTrail) {
        const dustCfg = dustTrail.config;
        const dustFolder = folder.addFolder('Dust (WebGPU)');
        dustFolder.add(dustCfg, 'offTrackSpawnRate', 0, 100, 1).name('spawn off-track');
        dustFolder.add(dustCfg, 'offTrackLifetimeSec', 0.2, 2, 0.01).name('life off-track');
        dustFolder.add(dustCfg, 'spread', 0.1, 4, 0.05).name('spread');
        dustFolder.add(dustCfg, 'yOffset', 0, 0.8, 0.005).name('height');
        dustFolder.add(dustCfg, 'upwardVelocityMin', 0, 1.5, 0.01).name('lift');
        dustFolder.add(dustCfg, 'upwardVelocityJitter', 0, 2, 0.01).name('lift jitter');
        dustFolder.add(dustCfg, 'gravity', 0, 6, 0.05).name('settle');
        dustFolder.add(dustCfg, 'drag', 0, 5, 0.05).name('drag');
        dustFolder.add(dustCfg, 'offTrackSizeMin', 2, 28, 0.05).name('size min');
        dustFolder.add(dustCfg, 'offTrackSizeMax', 4, 40, 0.05).name('size max');
        dustFolder.add(dustCfg, 'offTrackOpacity', 0, 1, 0.01).name('opacity');
        dustFolder.add(dustCfg, 'sizeGrowth', 0, 2.5, 0.05).name('growth');
        dustFolder.add(dustCfg, 'webgpuDissolveStart', 0.05, 0.95, 0.01).name('dissolve start');
        dustFolder.add(dustCfg, 'webgpuDissolveStrength', 0, 1, 0.01).name('dissolve');
        dustFolder.add(dustCfg, 'webgpuEdgeSoftness', 0.05, 0.45, 0.01).name('edge softness');
        dustFolder.add(dustCfg, 'webgpuNoiseScale', 0.5, 8, 0.1).name('internal flow');
        dustFolder.add(dustCfg, 'webgpuErosionFeather', 0.02, 0.5, 0.01).name('erosion feather');
        dustFolder.add(dustCfg, 'webgpuPuffContrast', 0.4, 2.5, 0.05).name('puff contrast');
        dustFolder.add(dustCfg, 'webgpuPuffStretch', 0, 0.8, 0.01).name('puff stretch');
        dustFolder.add(dustCfg, 'webgpuFadeStart', 0, 0.95, 0.01).name('fade start');
        dustFolder.add(dustCfg, 'webgpuFadePower', 0.2, 5, 0.05).name('fade power');
        dustFolder.add(dustCfg, 'webgpuEndScale', 0.05, 1, 0.01).name('end scale');
        dustFolder.add({ clear: () => dustTrail.clear() }, 'clear').name('Clear dust');
      }
    }

    folder.add({ clear: () => engineTrail.clear() }, 'clear').name('Clear trail');
    if (this.targets.particlesLayer) {
      folder.add({ save: () => void this.saveEngineTrailPreset(syncLayer) }, 'save').name('Save engine trail + dust');
    }
    folder.onChange(syncLayer);
  }

  private async saveEngineTrailPreset(syncLayer: () => void): Promise<void> {
    const particlesLayer = this.targets.particlesLayer;
    if (!particlesLayer) return;
    syncLayer();
    this.flashStatus('Writing particle preset...', true);
    const result = await saveDefaultsToSource(particlesLayer.file, particlesLayer.layer);
    if (result.ok) {
      const time = new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' });
      this.flashStatus(`Saved particles at ${time} (${result.bytes ?? '?'}b)`, true);
    } else {
      this.flashStatus(`Particle save failed: ${result.error ?? 'unknown'}`, false);
    }
  }

  private addSectionFolder(key: TuningSectionKey, label: string, icon: string, openDefault = false): GUI {
    const folder = this.gui.addFolder(label);
    if (openDefault) folder.open();
    else folder.close();
    this.sections.push({ key, navKey: tuningNavKeyFor(key), label, icon, folder });
    return folder;
  }

  private installSectionRail(): void {
    if (this.sections.length === 0) return;

    const title = document.createElement('div');
    title.className = 'tuning-panel-title';
    title.textContent = 'Tuning';

    const shell = document.createElement('div');
    shell.className = 'tuning-panel-section-shell';

    const railHost = document.createElement('div');
    railHost.className = 'tuning-panel-section-rail-host';

    const body = document.createElement('div');
    body.className = 'tuning-panel-section-body';

    for (const item of TUNING_NAV_ITEMS) {
      const panel = document.createElement('section');
      panel.className = 'tuning-panel-section-panel';
      panel.dataset.tuningSectionPanel = item.key;
      panel.setAttribute('aria-label', item.label);
      body.appendChild(panel);
      this.sectionPanels.set(item.key, panel);
    }

    for (const section of this.sections) {
      const panel = this.sectionPanels.get(section.navKey);
      panel?.appendChild(section.folder.domElement);
    }
    this.sectionPanels.get('racer')?.prepend(this.buildLayerSaveCard());
    this.sectionPanels.get('save')?.appendChild(this.buildLayerSaveCard());

    const activeKey = this.resolveInitialSectionKey();
    this.sectionRail = createIconRail<TuningNavKey>({
      ariaLabel: 'Tuning sections',
      className: 'tuning-panel-section-rail',
      itemClassName: 'tuning-panel-section-tab',
      items: TUNING_NAV_ITEMS,
      activeKey,
      buttonRole: 'tab',
      dataAttribute: 'tuning-section-tab',
      showLabels: true,
      onSelect: (key) => this.setActiveSection(key),
    });

    railHost.appendChild(this.sectionRail.root);
    shell.append(railHost, body);
    this.root.append(title, shell, this.statusElement);
    this.setActiveSection(activeKey);
  }

  private setActiveSection(key: TuningNavKey): void {
    this.sectionRail?.setActive(key);
    for (const [candidate, panel] of this.sectionPanels) {
      panel.hidden = candidate !== key;
    }
  }

  private resolveInitialSectionKey(): TuningNavKey {
    return this.sectionPanels.has('racer') ? 'racer' : TUNING_NAV_ITEMS[0].key;
  }

  private buildLayerSaveCard(): HTMLElement {
    const card = document.createElement('div');
    card.className = 'tuning-panel-layer-save';
    card.innerHTML = `
      <div class="tuning-panel-layer-file">
        <span aria-hidden="true">□</span>
        <code>podracer-mechanics.json</code>
        <b>OK</b>
      </div>
      <details class="tuning-panel-save-json">
        <summary>Save / JSON</summary>
        <div class="tuning-panel-save-json-actions">
          <button type="button">Save layer</button>
          <button type="button">Copy JSON</button>
          <button type="button">View JSON</button>
        </div>
      </details>
    `;
    return card;
  }
}

function tuningNavKeyFor(key: TuningSectionKey): TuningNavKey {
  switch (key) {
    case 'save':
      return 'physcam';
    case 'physics':
    case 'collision':
      return 'physics';
    case 'input':
      return 'input';
    case 'camera':
      return 'camera';
    case 'racer':
      return 'racer';
    case 'blur':
      return 'tune';
    case 'style':
    case 'lighting':
    case 'day':
    case 'presets':
    case 'shadow':
    case 'contact':
    case 'renderScale':
    case 'engineTrail':
    case 'pixel':
    case 'asphalt':
    case 'aggregate':
    case 'glint':
    case 'webgpuAsphalt':
      return 'style';
  }
}

// Re-export defaults so consumers know what the committed values are.
export { DEFAULT_PHYSICS, DEFAULT_CAMERA_TUNING, cameraNumericKeys, type CameraNumericKey };

function injectTuningPanelStyles(): void {
  if (document.getElementById('tuning-panel-section-rail-styles')) return;
  const style = document.createElement('style');
  style.id = 'tuning-panel-section-rail-styles';
  style.textContent = `
    .tuning-panel-with-rail {
      display: grid;
      width: 100%;
      min-width: 0;
      overflow: hidden;
      border: 1px solid rgba(89, 220, 255, 0.2);
      background: rgba(3, 8, 14, 0.62);
    }
    .tuning-panel-title {
      min-height: 36px;
      display: flex;
      align-items: center;
      padding: 0 10px;
      border-bottom: 1px solid rgba(89, 220, 255, 0.16);
      color: rgba(248, 251, 255, 0.92);
      font: 800 12px/1.2 ui-monospace, SFMono-Regular, Menlo, monospace;
      text-transform: uppercase;
      letter-spacing: 0;
    }
    .tuning-panel-section-shell {
      display: grid;
      grid-template-columns: 68px minmax(0, 1fr);
      min-width: 0;
      min-height: 260px;
    }
    .tuning-panel-section-rail-host {
      border-right: 1px solid rgba(89, 220, 255, 0.16);
      background: rgba(2, 7, 14, 0.34);
    }
    .tuning-panel-section-rail {
      padding: 6px 5px;
      gap: 6px;
    }
    .tuning-panel-section-tab.dev-icon-rail-button {
      width: 56px;
      min-width: 56px;
      min-height: 50px;
      border-color: rgba(89, 220, 255, 0.24);
      background: rgba(2, 10, 18, 0.72);
      color: rgba(217, 243, 255, 0.78);
    }
    .tuning-panel-section-tab .dev-icon-rail-glyph {
      width: 18px;
      height: 18px;
    }
    .tuning-panel-section-tab .dev-icon-rail-label.is-visible {
      font-size: 9px;
      line-height: 1;
    }
    .tuning-panel-section-tab.is-active {
      border-color: rgba(89, 220, 255, 0.86);
      color: #59dcff;
      background: rgba(5, 24, 34, 0.92);
    }
    .tuning-panel-section-body {
      min-width: 0;
      overflow: auto;
    }
    .tuning-panel-section-panel {
      min-width: 0;
    }
    .tuning-panel-section-panel[hidden] {
      display: none;
    }
    .tuning-panel-section-panel > .lil-gui.root,
    .tuning-panel-section-panel > .lil-gui {
      border-left: 0;
      border-right: 0;
      width: 100% !important;
    }
    .tuning-panel-section-panel > .lil-gui > .title {
      min-height: 36px;
      background: rgba(255, 255, 255, 0.03);
      border-bottom: 1px solid rgba(255, 255, 255, 0.08);
      color: rgba(248, 251, 255, 0.92);
      font: 800 12px/1.2 ui-monospace, SFMono-Regular, Menlo, monospace;
      letter-spacing: 0;
      text-transform: none;
    }
    .tuning-panel-status {
      min-height: 24px;
      padding: 6px 10px;
      border-top: 1px solid rgba(255, 255, 255, 0.06);
      color: rgba(217, 243, 255, 0.72);
      font: 11px/1.35 ui-monospace, SFMono-Regular, Menlo, monospace;
      transition: background-color 200ms ease;
    }
    .tuning-panel-layer-save {
      display: grid;
      gap: 12px;
      padding: 12px;
      border-bottom: 1px solid rgba(89, 220, 255, 0.16);
      background: rgba(2, 9, 16, 0.35);
    }
    .tuning-panel-layer-file {
      display: grid;
      grid-template-columns: auto minmax(0, 1fr) auto auto;
      align-items: center;
      gap: 10px;
      min-height: 42px;
      padding: 0 12px;
      border: 1px solid rgba(89, 220, 255, 0.18);
      background: rgba(0, 0, 0, 0.18);
      color: #66cf65;
      font: 700 12px/1.2 ui-monospace, SFMono-Regular, Menlo, monospace;
    }
    .tuning-panel-layer-file code {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      color: #66cf65;
    }
    .tuning-panel-layer-file b {
      color: #66cf65;
      font-weight: 800;
    }
    .tuning-panel-save-json {
      border: 1px solid rgba(89, 220, 255, 0.58);
      background: rgba(3, 15, 24, 0.42);
    }
    .tuning-panel-save-json summary {
      min-height: 52px;
      display: flex;
      align-items: center;
      padding: 0 14px;
      color: rgba(248, 251, 255, 0.92);
      cursor: pointer;
      font: 800 12px/1.2 ui-monospace, SFMono-Regular, Menlo, monospace;
      list-style: none;
    }
    .tuning-panel-save-json summary::-webkit-details-marker {
      display: none;
    }
    .tuning-panel-save-json summary::before {
      content: "⌄";
      margin-right: 12px;
      color: rgba(248, 251, 255, 0.92);
    }
    .tuning-panel-save-json-actions {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
      padding: 0 14px 14px;
    }
    .tuning-panel-save-json-actions button {
      min-height: 36px;
      padding: 0 14px;
      border: 1px solid rgba(89, 220, 255, 0.34);
      background: rgba(9, 18, 28, 0.82);
      color: rgba(248, 251, 255, 0.9);
      font: 700 11px/1 ui-monospace, SFMono-Regular, Menlo, monospace;
      cursor: pointer;
    }
  `;
  document.head.appendChild(style);
}
